import { getUserInfo } from "@/lib/storeManager";
import { DynamicModuleService } from "@/services/APIs";

const SCHEMA_NAME = "School Profile";

const APPROVAL_CACHE_PREFIX = "karuna_school_approval_v1";

export function getSchoolApprovalCacheKey(normalizedPhone: string): string {
  return `${APPROVAL_CACHE_PREFIX}:${normalizedPhone}`;
}

/** Session cache: instant UI for approved schools (no 2s disabled flash on reload/navigation). */
export function readCachedSchoolApprovalForPhone(
  normalizedPhone: string
): boolean | null {
  const p = normalizeSchoolMobile(normalizedPhone);
  if (!p) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(getSchoolApprovalCacheKey(p));
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as { approved: boolean };
    return typeof parsed.approved === "boolean" ? parsed.approved : null;
  } catch {
    return null;
  }
}

export function writeCachedSchoolApprovalForPhone(
  normalizedPhone: string,
  approved: boolean
) {
  const p = normalizeSchoolMobile(normalizedPhone);
  if (!p) {
    return;
  }
  try {
    sessionStorage.setItem(
      getSchoolApprovalCacheKey(p),
      JSON.stringify({ approved })
    );
  } catch {
    // ignore quota / private mode
  }
}

export function clearSchoolApprovalCacheForPhone(normalizedPhone: string) {
  const p = normalizeSchoolMobile(normalizedPhone);
  if (!p) {
    return;
  }
  try {
    sessionStorage.removeItem(getSchoolApprovalCacheKey(p));
  } catch {
    // ignore
  }
}

/** Call on school logout so the next user does not inherit approval state. */
export function clearAllSchoolApprovalCaches() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(`${APPROVAL_CACHE_PREFIX}:`)) {
        sessionStorage.removeItem(k);
      }
    }
  } catch {
    // ignore
  }
}

export function normalizeSchoolMobile(mobileNumber: string): string {
  return String(mobileNumber || "")
    .replace(/\D/g, "")
    .slice(0, 10);
}

export function getSchoolRecordId(record: any): string {
  return String(record?.id ?? record?.pk ?? record?._id ?? "").trim();
}

/** Status may live on the root or only on the full record after admin updates */
export function getSchoolProfileStatus(record: any): string {
  if (!record || typeof record !== "object") {
    return "";
  }
  const raw =
    record.status ??
    record.Status ??
    record.approval_status ??
    record.approvalStatus ??
    record.basicInfo?.status ??
    record.basic_info?.status ??
    record.basicGrid?.status ??
    record.basic_grid?.status ??
    record.data?.status ??
    record.data?.basicGrid?.status ??
    record.data?.basic_grid?.status;
  return String(raw ?? "")
    .toLowerCase()
    .trim();
}

const NESTED_STATUS_KEYS = [
  "basicGrid",
  "basic_grid",
  "basicInfo",
  "basic_info",
] as const;

function asPlainObject(value: unknown): Record<string, unknown> | null {
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Every status value on a record (root, nested sections, and `data`). */
export function collectSchoolProfileStatuses(record: any): string[] {
  const out: string[] = [];
  const visit = (src: Record<string, unknown> | null, depth: number) => {
    if (!src) {
      return;
    }
    for (const key of [
      "status",
      "Status",
      "approval_status",
      "approvalStatus",
    ]) {
      const v = src[key];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        out.push(String(v).toLowerCase().trim());
      }
    }
    for (const key of NESTED_STATUS_KEYS) {
      visit(asPlainObject(src[key]), depth + 1);
    }
    if (depth === 0) {
      visit(asPlainObject(src.data), depth + 1);
    }
  };
  visit(asPlainObject(record), 0);
  return out;
}

/**
 * Sources disagree after an admin update (grid_data / nested copies can stay
 * "pending"), so a decided status anywhere wins over pending/empty.
 */
export function getDecidedSchoolProfileStatus(
  record: any
): "approved" | "rejected" | "" {
  const decided = collectSchoolProfileStatuses(record).find(
    s => s === "approved" || s === "rejected"
  );
  return (decided as "approved" | "rejected" | undefined) ?? "";
}

/** Write status to the root and every nested copy so no source stays stale. */
export function applySchoolProfileStatus(
  payload: Record<string, unknown>,
  status: "approved" | "rejected",
  includeData = true
) {
  payload.status = status;
  for (const key of NESTED_STATUS_KEYS) {
    const nested = asPlainObject(payload[key]);
    if (nested) {
      payload[key] = { ...nested, status };
    }
  }
  const raw = payload.data;
  const data = includeData ? asPlainObject(raw) : null;
  if (
    data &&
    ["status", ...NESTED_STATUS_KEYS].some(key => data[key] !== undefined)
  ) {
    const updated = { ...data };
    applySchoolProfileStatus(updated, status, false);
    payload.data = typeof raw === "string" ? JSON.stringify(updated) : updated;
  }
}

/**
 * Single source for admin approval tabs. Unknown / empty → pending (queue for review).
 */
export function normalizeSchoolApprovalStatus(
  raw: string
): "pending" | "approved" | "rejected" {
  const t = (raw ?? "").toLowerCase().trim();
  if (t === "approved" || t === "rejected") {
    return t;
  }
  return "pending";
}

/** Schools tab: show approved rows and legacy/import rows without status in grid. */
export function isSchoolListedInManagement(record: any): boolean {
  if (!getSchoolRecordId(record)) {
    return false;
  }
  const status = getSchoolProfileStatus(record);
  return status !== "pending" && status !== "rejected";
}

/**
 * Grid list rows often omit `status`. Always merge getItemById when we have an id
 * so approval state matches admin after they approve.
 */
export async function fetchSchoolApprovalForCurrentUser(): Promise<{
  approved: boolean;
  found: boolean;
}> {
  const userInfo = getUserInfo();
  const mobileNumber = userInfo?.phone_number ?? "";
  if (!mobileNumber) {
    return { approved: false, found: false };
  }
  const normalizedMobile = normalizeSchoolMobile(mobileNumber);

  let allRecords: any[] = [];
  try {
    allRecords =
      await DynamicModuleService.getGridColItemsAllPages(SCHEMA_NAME);
  } catch {
    try {
      allRecords = await DynamicModuleService.getGridCoByConfigAllPages(
        SCHEMA_NAME,
        ["contact", "status"],
        {}
      );
    } catch {
      clearSchoolApprovalCacheForPhone(normalizedMobile);
      return { approved: false, found: false };
    }
  }

  const profileRecord = allRecords.find((record: any) => {
    const recordContact = String(
      record.contact ??
        record.contact_number ??
        record.mobile ??
        record.mobile_number ??
        record.phone ??
        record.phone_number ??
        ""
    ).trim();
    return normalizeSchoolMobile(recordContact) === normalizedMobile;
  });

  if (!profileRecord) {
    clearSchoolApprovalCacheForPhone(normalizedMobile);
    return { approved: false, found: false };
  }

  const id = profileRecord.id ?? profileRecord._id;
  let merged: any = profileRecord;

  if (id) {
    try {
      const full = await DynamicModuleService.getItemById(SCHEMA_NAME, id);
      const payload = full?.data ?? full?.result ?? full ?? {};
      if (payload && typeof payload === "object") {
        merged = { ...profileRecord, ...payload };
      }
    } catch (e) {
      console.warn("⚠️ school profile getItemById (approval):", e);
    }
  }

  const status = getSchoolProfileStatus(merged);
  const approved = status === "approved";
  writeCachedSchoolApprovalForPhone(normalizedMobile, approved);
  return { approved, found: true };
}
